import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Image,
  Text,
  View,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Platform,
} from "react-native";
import { AppFontStyle } from "../../../styles/AppFontStyle";
import { AppColors } from "../../../utility/AppColors";
import { AppImages } from "../../../utility/AppImages";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { SwitchCommon } from "../../../common/SwitchCommon";
import { fetchUserConnectionStatus } from "../../../services/stream";
import { updateStreamPlatform } from "../../../services/eventsAPI";
import YoutubeEmbeddingStepsPopup from "./YoutubeEmbeddingStepsPopup";
import { useStreamingOAuth } from "../../../hooks/useStreamingOAuth";
import Separator from "../../Separator";
import { useWsProvider } from "../../../context/NativeApplicationContext";
import { toast } from "react-toastify";
import { LinearGradient } from "expo-linear-gradient";

interface StreamEventDetails {
  eventID: string;
  streamingPlatforms: {
    organiser: string[];
    challenger: string[];
    [key: string]: string[];
  };
  organiserId: string;
  organizerYoutubeStream: boolean;
  challengerYoutubeStream: boolean;
  organizerEmbedded: boolean;
  challengerEmbedded: boolean;
  eventStartTime: string;
  userYoutubeStreamConnected: boolean;
  userTwitchStreamConnected: boolean;
}

interface APIResponse {
  statusCode: number;
  body?: any;
}

const safeAsync = async <T,>(asyncFn: () => Promise<T>): Promise<T | null> => {
  try {
    return await asyncFn();
  } catch (error) {
    return null;
  }
};

interface PlatformUsernames {
  twitch: string | null;
  youtube: string | null;
  squadifi: string | null;
}

interface SwitchLoadingState {
  twitch: boolean;
  youtube: boolean;
}

interface ConnectionStatusResponse extends APIResponse {
  body?: {
    twitchUsername?: string;
    youtubeUsername?: string;
    squadifiUsername?: string;
  };
}

interface StreamViewProps {
  styles: Record<string, any>;
  eventDetails: StreamEventDetails;
  accessToken: string;
  username: string;
  setDetails: () => void;
  isYoutubeSelected: boolean;
  setIsYoutubeSelected: (val: boolean) => void;
  isTwitchSelected: boolean;
  setIsTwitchSelected: (val: boolean) => void;
  isStreamPlatformSetupCompleted: boolean;
  handleConnectToBroadcaster: () => void;
  isConnecting: boolean;
  isDownloading: boolean;
  downloadProgress: number;
  isDownloaded: boolean;
}

const StreamView = ({
  styles,
  eventDetails,
  accessToken,
  username,
  setDetails,
  isYoutubeSelected,
  setIsYoutubeSelected,
  isTwitchSelected,
  setIsTwitchSelected,
  isStreamPlatformSetupCompleted,
  handleConnectToBroadcaster,
  isConnecting,
  isDownloading,
  downloadProgress,
  isDownloaded,
}: StreamViewProps) => {
  const isMountedRef = useRef(true);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const deviceSize = useDeviceSize();
  const [fetchingPlatformStatus, setFetchingPlatformStatus] = useState(true);
  const [switchLoading, setSwitchLoading] = useState<SwitchLoadingState>({
    twitch: false,
    youtube: false,
  });
  const [isLessThanOneHour, setIsLessThanOneHour] = useState(false);
  const [createStreamTooltip, setCreateStreamTooltip] = useState(false);
  const [createYoutubeStreamTooltip, setCreateYoutubeStreamTooltip] =
    useState(false);
  const [youtubeStepsTooltip, setYoutubeStepsTooltip] = useState(false);
  const [platformUsernames, setPlatformUsernames] = useState<PlatformUsernames>(
    {
      twitch: null,
      youtube: null,
      squadifi: null,
    }
  );

  const isOrganizer = useMemo(
    () => eventDetails?.organiserId === username,
    [eventDetails?.organiserId, username]
  );
  const {
    clearError,
    streamStarted,
    startRecording,
    recordingFile,
    nativeAppConnectionState,
    nativeAppStatus,
    isNativeAppConnected,
    connectWithDeeplink,
  } = useWsProvider();


  const userRole = useMemo(
    () => (isOrganizer ? "organiser" : "challenger"),
    [isOrganizer]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      timeoutRefs.current.forEach(clearTimeout);
      timeoutRefs.current = [];
    };
  }, []);

  useEffect(() => {
    if (!eventDetails?.streamingPlatforms) return;
    const userStreamingPlatforms =
      eventDetails.streamingPlatforms?.[userRole] || [];
    setIsTwitchSelected(userStreamingPlatforms.includes("Twitch"));
    setIsYoutubeSelected(userStreamingPlatforms.includes("YouTube"));
  }, [
    eventDetails?.streamingPlatforms,
    userRole,
    setIsTwitchSelected,
    setIsYoutubeSelected,
  ]);

  useEffect(() => {
    const eventStartTime = eventDetails?.eventStartTime;
    if (!eventStartTime || !isMountedRef.current) return;

    const checkTimeStatus = () => {
      if (!isMountedRef.current) return;
      const startTime = new Date(eventStartTime);
      const currentDate = new Date();
      const timeDifference = startTime.getTime() - currentDate.getTime();
      setIsLessThanOneHour(timeDifference <= 3600000 && timeDifference > 0);
    };

    checkTimeStatus();

    intervalRef.current = setInterval(checkTimeStatus, 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [eventDetails?.eventStartTime]);

  const fetchStatus = useCallback(async () => {
    if (!eventDetails?.eventID || !accessToken || !isMountedRef.current) return;

    try {
      if (isMountedRef.current) {
        setFetchingPlatformStatus(true);
      }

      const response = await safeAsync(() =>
        fetchUserConnectionStatus(accessToken)
      );

      if (!isMountedRef.current || !response) {
        if (isMountedRef.current) {
          setFetchingPlatformStatus(false);
        }
        return;
      }

      if (response.statusCode === 200 && response.body) {
        if (isMountedRef.current) {
          setPlatformUsernames({
            twitch: response.body.twitchUsername || null,
            youtube: response.body.youtubeUsername || null,
            squadifi: response.body.squadifiUsername || null,
          });
        }

        const timeout1 = setTimeout(() => {
          if (isMountedRef.current) {
            setFetchingPlatformStatus(false);
          }
        }, 800);

        const timeout2 = setTimeout(() => {
          // Broadcaster status is now managed by context
        }, 2500);

        timeoutRefs.current.push(timeout1, timeout2);
      } else {
        if (isMountedRef.current) {
          setFetchingPlatformStatus(false);
        }
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        setFetchingPlatformStatus(false);
        toast.error(`Error fetching connection status: ${error.message}`);
      }
    }
  }, [eventDetails?.eventID, accessToken]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const userYoutubeStreamConnected = useMemo(
    () => eventDetails?.userYoutubeStreamConnected ?? false,
    [eventDetails?.userYoutubeStreamConnected]
  );

  const userTwitchStreamConnected = useMemo(
    () => eventDetails?.userTwitchStreamConnected ?? false,
    [eventDetails?.userTwitchStreamConnected]
  );

  const handleSwitchToggle = useCallback(
    async (
      platform: "Twitch" | "YouTube",
      isEnabled: boolean,
      setSwitchState: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      if (!isMountedRef.current || !eventDetails?.eventID || !accessToken) {
        if (isMountedRef.current) {
          toast.error("Missing event or auth details.");
        }
        return;
      }

      const otherSwitch =
        platform === "Twitch" ? isYoutubeSelected : isTwitchSelected;
      const willEnableMultiStream = isEnabled && otherSwitch;
      const platformKey = platform.toLowerCase() as "twitch" | "youtube";

      try {
        if (isMountedRef.current) {
          setSwitchLoading((prev) => ({ ...prev, [platformKey]: true }));
          setSwitchState(isEnabled);
        }

        const response = await safeAsync(() =>
          updateStreamPlatform(
            accessToken,
            eventDetails.eventID,
            platform,
            isEnabled,
            willEnableMultiStream
          )
        );

        if (!isMountedRef.current || !response) return;

        if (response.statusCode === 200) {
          clearError();
        } else {
          throw new Error(
            response.body?.error || "Failed to update streaming platform."
          );
        }
      } catch (error: any) {
        if (isMountedRef.current) {
          setSwitchState(!isEnabled);
          toast.error(`Error updating ${platform}: ${error.message}`);
        }
      } finally {
        if (isMountedRef.current) {
          setSwitchLoading((prev) => ({ ...prev, [platformKey]: false }));
        }
      }
    },
    [
      accessToken,
      eventDetails?.eventID,
      isYoutubeSelected,
      isTwitchSelected,
      userRole,
      clearError,
    ]
  );

  const handleOutsideClick = useCallback(() => {
    if (createStreamTooltip) setCreateStreamTooltip(false);
    if (createYoutubeStreamTooltip) setCreateYoutubeStreamTooltip(false);
    if (youtubeStepsTooltip) setYoutubeStepsTooltip(false);
  }, [createStreamTooltip, createYoutubeStreamTooltip, youtubeStepsTooltip]);

  const redirectUri = useMemo(
    () => `/events/ongoing?eventID=${eventDetails?.eventID}&tab=streaming`,
    [eventDetails?.eventID]
  );

  const { redirectToYouTubeOAuth, redirectToTwitchOAuth } = useStreamingOAuth(
    accessToken,
    redirectUri
  );

  const renderPlatformStatus = (isConnected: boolean) => {
    if (fetchingPlatformStatus) {
      return (
        <Text style={[AppFontStyle.SEMIBOLD_ANYBODY_12, { color: "#FFA500" }]}>
          Checking connection...
        </Text>
      );
    }
    return (
      <Text
        style={[
          AppFontStyle.SEMIBOLD_ANYBODY_12,
          { color: isConnected ? "#409101" : "#C3081B" },
        ]}
      >
        {isConnected ? "Connected" : "Disconnected"}
      </Text>
    );
  };

  const renderActionButton = (
    isConnected: boolean,
    onConnect: () => void,
    switchComponent: JSX.Element,
    platform: string
  ) => {
    const platformKey = platform?.toLowerCase() as "twitch" | "youtube";
    const isLoading = fetchingPlatformStatus || switchLoading[platformKey];
    const isMobile = (deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM;

    if (fetchingPlatformStatus) {
      return (
        <View
          style={[
            styles.btnStyle,
            {
              backgroundColor: "#4B5563",
              alignItems: "center",
              justifyContent: "center",
              height: isMobile ? 28 : 40,
              width: isMobile ? undefined : 100,
              paddingHorizontal: isMobile ? 24 : undefined,
              borderRadius: isMobile ? 6 : undefined,
            },
          ]}
        >
          <Text style={{ color: "#9CA3AF", fontSize: 12, fontWeight: isMobile ? "600" : "400" }}>Checking...</Text>
        </View>
      );
    }

    if (isConnected) {
      return (
        <View style={{ opacity: isLoading ? 0.6 : 1 }}>
          {switchComponent}
          {isLoading && (
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_10,
                { color: "#FFA500", marginTop: 2 },
              ]}
            >
              Updating...
            </Text>
          )}
        </View>
      );
    }

    // Mobile: Red gradient button
    if (isMobile) {
      return (
        <LinearGradient
          colors={["#DC2626", "#991B1B"]}
          start={[0, 0]}
          end={[1, 0]}
          style={{
            height: 28,
            paddingVertical: 10,
            paddingHorizontal: 24,
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 6,
          }}
        >
          <TouchableOpacity onPress={onConnect}>
            <Text style={{ color: "white", fontWeight: "600", fontSize: 12 }}>Connect</Text>
          </TouchableOpacity>
        </LinearGradient>
      );
    }

    // Desktop: Original purple button
    return (
      <TouchableOpacity
        style={[
          styles.btnStyle,
          {
            backgroundColor: AppColors.appViolentColor_6D08C3,
            alignItems: "center",
            justifyContent: "center",
            height: 40,
            width: 100,
          },
        ]}
        onPress={onConnect}
      >
        <Text style={{ color: "white" }}>Connect</Text>
      </TouchableOpacity>
    );
  };

  const handleConnect = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      await connectWithDeeplink();
    } catch (error: any) {
      if (isMountedRef.current) {
        toast.error("Failed to connect to KO Broadcaster. Please ensure it's installed and running.");
      }
    }
  }, [connectWithDeeplink]);

  const renderBroadcasterStatus = () => {
    if (nativeAppConnectionState === 'native-app-connecting') {
      return (
        <View
          style={{
            gap: 10,
            padding: 10,
            marginTop: 10,
            paddingVertical: 20,
            backgroundColor: "#FFA50033",
            borderRadius: 5,
            borderWidth: 1,
            borderColor: "#FFA500",
            minHeight: 80,
            justifyContent: "center",
          }}
        >
          <Text
            style={[AppFontStyle.SEMIBOLD_ANYBODY_14, { color: "#FFA500" }]}
          >
            Connecting to KO Broadcaster...
          </Text>
        </View>
      );
    }

    if (isDownloading) {
      return (
        <View
          style={{
            padding: 10,
            marginTop: 10,
            paddingVertical: 20,
            backgroundColor: "#1E1B4B",
            borderColor: AppColors.blue_233DFF,
            borderWidth: 1,
            borderRadius: 5,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: 80,
          }}
        >
          <Text
            style={[
              AppFontStyle.SEMIBOLD_ANYBODY_14,
              { color: AppColors.blue_233DFF },
            ]}
          >
            KO Broadcaster downloading
          </Text>
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_14,
              { color: AppColors.blue_233DFF },
            ]}
          >
            {downloadProgress}%
          </Text>
        </View>
      );
    }

    return (
      <View
        style={{
          gap: 10,
          padding: 10,
          marginTop: 10,
          paddingVertical: 20,
          backgroundColor: isNativeAppConnected ? "#86EFAC33" : "#C3081B33",
          borderRadius: 5,
          borderWidth: 1,
          borderColor: isNativeAppConnected ? "#86EFAC" : "#C3081B",
          minHeight: 80,
          justifyContent: "center",
        }}
      >
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_14,
            { color: isNativeAppConnected ? "#86EFAC" : "#C3081B" },
          ]}
        >{isNativeAppConnected
          ? `KO Broadcaster is Connected - ${nativeAppStatus?.state || 'ready'}`
          : "KO Broadcaster is Disconnected"
          }</Text>
        {isNativeAppConnected && (
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              { color: AppColors.white, marginTop: 5 },
            ]}
          >
            🎉 Awesome! We'll handle all your streaming automatically.
          </Text>
        )}
        {streamStarted && (
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              { color: AppColors.white, marginTop: 5 },
            ]}
          >
            Streaming: Active
          </Text>
        )}
        {startRecording && (
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              { color: AppColors.white, marginTop: 5 },
            ]}
          >
            Recording: Active
          </Text>
        )}
        {recordingFile && (
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              { color: AppColors.white, marginTop: 5 },
            ]}
          >
            Recording File: {recordingFile}
          </Text>
        )}
      </View>
    );
  };

  return (
    <>
      <TouchableWithoutFeedback onPress={handleOutsideClick}>
        <LinearGradient
          colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
          start={[0, 0]}
          end={[1, 1]}
          style={{
            marginTop: (deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM ? 0 : 10,
            padding: (deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM ? 8 : 20,
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 10,
              gap: 10,
            }}
          >
            <View
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                backgroundColor: isStreamPlatformSetupCompleted
                  ? AppColors.green_00B536
                  : "#DC2626",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isStreamPlatformSetupCompleted ? (
                <Image
                  source={AppImages.icComplete}
                  style={{ width: 18, height: 18 }}
                />
              ) : (
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_20,
                    { color: AppColors.white, fontSize: 14 },
                  ]}
                >
                  1
                </Text>
              )}
            </View>
            <Text style={((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM)
              ? [AppFontStyle.BOLD_ANYBODY_14, { lineHeight: 16 }]
              : [AppFontStyle.BOLD_ANYBODY_20]}>
              Connect Streaming Platform
            </Text>
          </View>
          {/* Platform cards container - vertical on mobile, horizontal on desktop */}
          <View style={{
            display: "flex",
            flexDirection: (deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM ? "column" : "row",
            gap: 10
          }}>
            {/* Twitch Card */}
            <View
              style={{
                backgroundColor: userTwitchStreamConnected
                  ? "rgba(220, 38, 38, 0.20)"
                  : ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? undefined : "#1F1C1C",
                borderColor: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM)
                  ? (userTwitchStreamConnected ? "#DC2626" : "#374151")
                  : "#DC2626",
                borderWidth: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM)
                  ? (userTwitchStreamConnected ? 1 : 0.8)
                  : 1,
                borderRadius: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM)
                  ? (userTwitchStreamConnected ? 4 : 8)
                  : 4,
                padding: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? 8 : 10,
                width: (deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM ? "100%" : "50%",
                height: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) && !userTwitchStreamConnected ? 47 : undefined,
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                ...((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM
                  ? (!userTwitchStreamConnected && { background: "linear-gradient(180deg, #1F2937 0%, #111827 100%)" })
                  : {}),
              }}
            >
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? 10 : 30 }}
              >
                <Image
                  source={AppImages.icTwitchLogo}
                  style={{ width: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? 31 : 48, height: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? 31 : 48 }}
                />
                <View>
                  <Text style={((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? AppFontStyle.BOLD_ANYBODY_14 : AppFontStyle.BOLD_ANYBODY_18}>Twitch</Text>
                  {renderPlatformStatus(userTwitchStreamConnected)}
                  {!((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) && platformUsernames.twitch && (
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_12,
                        { color: AppColors.white, marginTop: 4, opacity: 0.8 },
                      ]}
                    >
                      @{platformUsernames.twitch}
                    </Text>
                  )}
                </View>
              </View>
              <View
                style={{ alignItems: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? "flex-end" : "flex-start", justifyContent: "center" }}
              >
                {renderActionButton(
                  userTwitchStreamConnected,
                  () => {
                    // Store event details for post-OAuth redirect
                    localStorage.setItem("oauthRedirectEventID", eventDetails?.eventID || "");
                    localStorage.setItem("oauthRedirectTab", "streaming");
                    redirectToTwitchOAuth();
                  },
                  <SwitchCommon
                    switchState={isTwitchSelected}
                    setSwitchState={(state: boolean) =>
                      handleSwitchToggle("Twitch", state, setIsTwitchSelected)
                    }
                    color="red"
                  />,
                  "Twitch"
                )}
              </View>
            </View>
            {/* YouTube Card */}
            <View
              style={{
                backgroundColor: userYoutubeStreamConnected
                  ? "rgba(220, 38, 38, 0.20)"
                  : ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? undefined : "#1F1C1C",
                borderColor: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM)
                  ? (userYoutubeStreamConnected ? "#DC2626" : "#374151")
                  : "#DC2626",
                borderWidth: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM)
                  ? (userYoutubeStreamConnected ? 1 : 0.8)
                  : 1,
                borderRadius: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM)
                  ? (userYoutubeStreamConnected ? 4 : 8)
                  : 4,
                padding: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? 8 : 10,
                width: (deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM ? "100%" : "50%",
                height: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) && !userYoutubeStreamConnected ? 47 : undefined,
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                ...((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM
                  ? (!userYoutubeStreamConnected && { background: "linear-gradient(180deg, #1F2937 0%, #111827 100%)" })
                  : {}),
              }}
            >
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? 10 : 30 }}
              >
                <Image
                  source={AppImages.icYoutubeLogo}
                  style={{ width: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? 31 : 48, height: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? 31 : 48 }}
                />
                <View>
                  <Text style={((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? AppFontStyle.BOLD_ANYBODY_14 : AppFontStyle.BOLD_ANYBODY_18}>YouTube</Text>
                  {renderPlatformStatus(userYoutubeStreamConnected)}
                  {!((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) && platformUsernames.youtube && (
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_12,
                        { color: AppColors.white, marginTop: 4, opacity: 0.8 },
                      ]}
                    >
                      @{platformUsernames.youtube}
                    </Text>
                  )}
                </View>
              </View>
              <View
                style={{ alignItems: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? "flex-end" : "flex-start", justifyContent: "center" }}
              >
                {renderActionButton(
                  userYoutubeStreamConnected,
                  () => {
                    // Store event details for post-OAuth redirect
                    localStorage.setItem("oauthRedirectEventID", eventDetails?.eventID || "");
                    localStorage.setItem("oauthRedirectTab", "streaming");
                    redirectToYouTubeOAuth();
                  },
                  <SwitchCommon
                    switchState={isYoutubeSelected}
                    setSwitchState={(state: boolean) =>
                      handleSwitchToggle("YouTube", state, setIsYoutubeSelected)
                    }
                    color="red"
                  />,
                  "YouTube"
                )}
              </View>
            </View>
          </View>
        </LinearGradient>
      </TouchableWithoutFeedback>
      {/* Mobile: Simplified Broadcaster section with desktop message */}
      {(deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM ? (
        <View
          style={{
            marginTop: 10,
            marginBottom: 15,
            height: 178,
            padding: 8,
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 6,
            alignSelf: "stretch",
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            backgroundColor: "rgba(31, 41, 55, 0.5)",
          }}
        >
          <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
            Broadcaster Software connection
          </Text>
          {/* Separator line */}
          <View style={{ height: 1, backgroundColor: "#40324C", alignSelf: "stretch" }} />
          <Text style={[AppFontStyle.SEMIBOLD_ANYBODY_14, { color: AppColors.white, marginTop: 4 }]}>
            KO Broadcaster is our official streaming software
          </Text>
          <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: AppColors.white }]}>
            It only takes 2 minutes to download.{"\n"}
            Download and install the KO Broadcaster, and we will handle all for you
          </Text>
          {/* Mobile Warning Message */}
          <View
            style={{
              padding: 6,
              paddingHorizontal: 8,
              justifyContent: "flex-start",
              alignItems: "flex-start",
              alignSelf: "stretch",
              borderRadius: 8,
              backgroundColor: "#545FC7",
              marginTop: 10,
            }}
          >
            <Text style={[AppFontStyle.SEMIBOLD_ANYBODY_12, { color: AppColors.white }]}>
              Please use desktop, this feature is not available in mobile app
            </Text>
          </View>
        </View>
      ) : (
        /* Desktop: Full Broadcaster section with connection logic */
        <TouchableWithoutFeedback onPress={handleOutsideClick}>
          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={[0, 0]}
            end={[1, 1]}
            style={{
              marginTop: 10,
              padding: 20,
              borderRadius: 8,
              borderWidth: 0.8,
              borderColor: "#374151",
            }}
          >
            {/* Header Row */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginBottom: 10,
                gap: 10,
              }}
            >
              <View
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 15,
                  backgroundColor: nativeAppConnectionState === 'native-app-connecting'
                    ? "#FFA500"
                    : isNativeAppConnected
                      ? AppColors.green_00B536
                      : "#DC2626",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {nativeAppConnectionState === 'native-app-connecting' ? (
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_20,
                      { color: AppColors.white, fontSize: 14 },
                    ]}
                  >
                    ?
                  </Text>
                ) : isNativeAppConnected ? (
                  <Image
                    source={AppImages.CheckInTick}
                    style={{ width: 18, height: 18 }}
                  />
                ) : (
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_20,
                      { color: AppColors.white, fontSize: 14 },
                    ]}
                  >
                    2
                  </Text>
                )}
              </View>
              <Text style={[AppFontStyle.BOLD_ANYBODY_20]}>
                Broadcaster Software connection
              </Text>
              {/* Connect Button - Blue */}
              {nativeAppConnectionState !== 'native-app-connecting' && !isDownloading && !isNativeAppConnected && (
                <TouchableOpacity
                  style={{
                    display: "flex",
                    height: 40,
                    paddingVertical: 10,
                    paddingHorizontal: 24,
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 10,
                    borderRadius: 6,
                    backgroundColor: "#09F",
                    marginLeft: "auto",
                    opacity: isConnecting ? 0.7 : 1,
                  }}
                  onPress={handleConnect}
                  disabled={isConnecting}
                >
                  <Text
                    style={{
                      color: "#FFF",
                      fontWeight: "600",
                      fontSize: 14,
                    }}
                  >
                    {isConnecting ? "Connecting..." : "Connect"}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
            <Separator
              color={AppColors.white}
              height={1}
              marginTop={10}
              marginBottom={10}
              marginHorizontal={0}
            />
            {renderBroadcasterStatus()}

            {/* KO Broadcaster Description Section */}
            {nativeAppConnectionState !== 'native-app-connecting' && !isNativeAppConnected && (
              <View style={{ marginTop: 20 }}>
                <Text
                  style={{
                    color: "#FFF",
                    fontSize: 15.9,
                    fontWeight: "500",
                    lineHeight: 24,
                    marginBottom: 8,
                  }}
                >
                  KO Broadcaster is our official streaming software
                </Text>
                <Text
                  style={{
                    color: "#FFF",
                    fontSize: 14,
                    fontWeight: "500",
                    lineHeight: 24,
                  }}
                >
                  It only takes 2 minutes to download.
                </Text>
                <Text
                  style={{
                    color: "#FFF",
                    fontSize: 14,
                    fontWeight: "500",
                    lineHeight: 24,
                  }}
                >
                  Download and install the KO Broadcaster, and we will handle all for you
                </Text>
                <Text
                  style={{
                    color: "#FFF",
                    fontSize: 14,
                    fontWeight: "500",
                    lineHeight: 24,
                    marginBottom: 16,
                  }}
                >
                  If you already download it, click on connect button to connect it.
                </Text>

                {/* Download Button - Red Gradient */}
                <LinearGradient
                  colors={["#DC2626", "#991B1B"]}
                  start={[0, 0]}
                  end={[1, 0]}
                  style={{
                    display: "flex",
                    height: 40,
                    paddingVertical: 10,
                    paddingHorizontal: 24,
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 10,
                    borderRadius: 6,
                    alignSelf: "flex-end",
                  }}
                >
                  <TouchableOpacity
                    onPress={handleConnectToBroadcaster}
                    disabled={isConnecting}
                    style={{
                      opacity: isConnecting ? 0.7 : 1,
                    }}
                  >
                    <Text
                      style={{
                        color: "#FFF",
                        fontWeight: "600",
                        fontSize: 14,
                      }}
                    >
                      {isConnecting ? "Downloading..." : "Download KO Broadcaster"}
                    </Text>
                  </TouchableOpacity>
                </LinearGradient>
              </View>
            )}
          </LinearGradient>
        </TouchableWithoutFeedback>
      )}
      {youtubeStepsTooltip && (
        <YoutubeEmbeddingStepsPopup
          showModal={youtubeStepsTooltip}
          setShowModal={setYoutubeStepsTooltip}
        />
      )}
    </>
  );
};

export default React.memo(StreamView);
